import React, { useMemo, useState } from "react";
import { Quote, Star, PencilLine } from "lucide-react";
import { TESTIMONIALS, MAGIC_PHOTOS } from "../data/cruiseData";

const FILTERS = ["All Events", "Wedding", "Corporate", "NYE Cruise", "Birthday", "Hens Party", "BYO Cruise", "Family", "Other"];

// Stable color per initial (gradients)
const AVATAR_GRADS = [
 "from-fuchsia-500 to-rose-500",
 "from-sky-500 to-cyan-500",
 "from-amber-400 to-orange-500",
 "from-violet-500 to-fuchsia-500",
 "from-emerald-500 to-teal-500",
 "from-pink-500 to-rose-500",
 "from-blue-500 to-indigo-600",
 "from-orange-400 to-pink-500",
];

function getInitials(name) {
 return name.split(/[\s&·]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
}
function gradFor(name) {
 let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
 return AVATAR_GRADS[h % AVATAR_GRADS.length];
}

export default function TestimonialsSection({ limit }) {
 const [filter, setFilter] = useState("All Events");
 const reviews = useMemo(() => {
 let list;
 if (filter === "All Events") list = TESTIMONIALS;
 else if (filter === "Other") {
 const known = ["Wedding", "Corporate", "NYE Cruise", "Birthday", "Hens Party", "BYO Cruise", "Family"];
 list = TESTIMONIALS.filter(t => !known.includes(t.event_type));
 } else {
 list = TESTIMONIALS.filter(t => t.event_type === filter);
 }
 return limit ? list.slice(0, limit) : list;
 }, [filter, limit]);

 return (
 <section className="relative overflow-hidden" data-testid="testimonials-section">
 {/* Solid navy backdrop with subtle photo blend (no harsh edges) */}
 <div className="absolute inset-0 z-0 bg-[hsl(225,82%,14%)]">
 <div
 className="absolute inset-0 bg-no-repeat bg-cover bg-center opacity-30"
 style={{
 backgroundImage: `url("${MAGIC_PHOTOS.corporateChampagne}")`,
 filter: "saturate(1.05)",
 }}
 />
 {/* Subtle vignette so edges fade to deep navy */}
 <div className="absolute inset-0" style={{ background: "radial-gradient(120% 80% at 50% 30%, hsla(225,82%,14%,0.55) 0%, hsla(225,82%,12%,0.95) 70%, hsl(225,85%,9%) 100%)" }} />
 {/* Decorative blurred orbs */}
 <div className="absolute -top-20 -left-20 w-[28rem] h-[28rem] rounded-full bg-amber-400/12 blur-3xl" />
 <div className="absolute -bottom-24 -right-20 w-[32rem] h-[32rem] rounded-full bg-sky-400/12 blur-3xl" />
 </div>

 <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
 {/* Header */}
 <div className="text-center max-w-3xl mx-auto">
 <div className="text-[11px] tracking-[0.32em] font-bold text-amber-300 uppercase">Real Reviews</div>
 <h2 className="text-white mt-5 text-5xl md:text-6xl lg:text-7xl font-black leading-[1.0] tracking-tight" data-testid="testi-headline">
 <span className="text-white">Loved by Sydneysiders</span>
 <span className="block italic font-light text-amber-300 mt-2">&amp; visitors alike.</span>
 </h2>
 <div className="mt-6 flex items-center justify-center gap-2 text-white/80 text-sm">
 <div className="flex gap-0.5">{Array.from({length: 5}).map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
 <span className="font-bold text-white">5.0</span>
 <span className="text-white/60">·</span>
 <span>{TESTIMONIALS.length}+ verified reviews</span>
 </div>
 </div>

 {/* Filters */}
 <div className="mt-10 flex flex-wrap items-center justify-center gap-2" data-testid="testi-filters">
 {FILTERS.map(f => {
 const active = filter === f;
 return (
 <button
 key={f}
 onClick={() => setFilter(f)}
 className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${active ? "bg-amber-400 text-[hsl(225,80%,14%)] shadow-lg shadow-amber-400/30" : "bg-white/8 text-white/80 hover:bg-white/15 ring-1 ring-white/15"}`}
 data-testid={`testi-filter-${f.replace(/\s|\//g, "-").toLowerCase()}`}
 >
 {f}
 </button>
 );
 })}
 <a href="#enquire" className="ml-1 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider bg-white/8 text-white/85 hover:bg-white/15 ring-1 ring-white/15 inline-flex items-center gap-1.5" data-testid="testi-write-review">
 <PencilLine className="w-3.5 h-3.5" /> Write a review
 </a>
 </div>

 {/* Cards */}
 <div className={`mt-12 grid gap-5 ${limit === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="testi-grid">
 {reviews.map((t, i) => (
 <article
 key={`${t.name}-${i}`}
 className="bg-white/[0.04] backdrop-blur-sm ring-1 ring-white/10 rounded-2xl p-6 flex flex-col hover:ring-amber-400/40 hover:bg-white/[0.07] transition-all"
 data-testid={`testi-card-${i}`}
 >
 <Quote className="w-7 h-7 text-amber-300/90 mb-4" />
 <div className="flex gap-0.5 mb-3">{Array.from({length: 5}).map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
 <p className="text-white/85 text-sm leading-relaxed flex-1">"{t.quote}"</p>
 <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
 <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradFor(t.name)} text-white font-bold text-xs flex items-center justify-center shadow-md`} aria-hidden="true, ">
 {getInitials(t.name)}
 </div>
 <div className="min-w-0">
 <div className="text-white text-sm font-bold truncate">{t.name}</div>
 <div className="text-white/55 text-[11px] uppercase tracking-wider truncate">{t.event_type} · {t.date}</div>
 </div>
 </div>
 </article>
 ))}
 </div>

 {reviews.length === 0 && (
 <div className="mt-12 text-center text-white/70 text-sm" data-testid="testi-empty">
 No reviews under <strong className="text-amber-300">{filter}</strong> yet, be the first to write one.
 </div>
 )}
 </div>
 </section>
 );
}
